import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Grid3X3, 
  List,
  User,
  Mail,
  Phone,
  MapPin,
  Star,
  Eye,
  CheckCircle,
  XCircle,
  CreditCard,
  Download,
  Users
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../contexts/AuthContext';
import AdminLayout from '../common/AdminLayout';
import Swal from 'sweetalert2';

interface Client {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address?: string;
  parish?: string;
  subscription_status: 'free' | 'premium';
  subscription_start_date?: string;
  subscription_end_date?: string;
  status: 'active' | 'inactive' | 'blocked';
  email_verified: boolean;
  phone_verified: boolean;
  profile_picture?: string;
  created_at: string;
  updated_at: string;
  total_bookings?: number;
  completed_bookings?: number;
  cancelled_bookings?: number;
  total_spent?: number;
  last_booking_date?: string;
}

const Clients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSubscription, setFilterSubscription] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [showClientModal, setShowClientModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const { admin } = useAuth();

  // Form state
  const [clientForm, setClientForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    parish: '',
    subscription_status: 'free' as 'free' | 'premium',
    status: 'active' as 'active' | 'inactive' | 'blocked',
    email_verified: false,
    phone_verified: false
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients');
      
      if (response.data.success) {
        setClients(response.data.clients || []);
      } else {
        setClients([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let response;
      if (editingClient) {
        response = await api.put(`/clients/${editingClient.id}`, clientForm);
      } else {
        response = await api.post('/clients', clientForm);
      }
      
      if (response.data.success) {
        setShowClientModal(false);
        setEditingClient(null);
        resetForm();
        fetchClients();
      }
    } catch (error: any) {
      console.error('Failed to save client:', error);
    }
  };

  const handleDeleteClient = async (id: number) => {
    const result = await Swal.fire({
      title: 'Confirm Delete',
      text: 'Are you sure you want to delete this client?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/clients/${id}`);
        fetchClients();
        Swal.fire({
          title: 'Success!',
          text: 'Client deleted successfully',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745'
        });
      } catch (error) {
        console.error('Failed to delete client:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete client',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      }
    }
  };

  const openEditClient = (client: Client) => {
    setEditingClient(client);
    setClientForm({
      first_name: client.first_name,
      last_name: client.last_name,
      email: client.email,
      phone: client.phone,
      address: client.address || '',
      parish: client.parish || '',
      subscription_status: client.subscription_status,
      status: client.status,
      email_verified: client.email_verified,
      phone_verified: client.phone_verified
    });
    setShowClientModal(true);
  };

  const openClientDetails = (client: Client) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  const resetForm = () => {
    setClientForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      parish: '',
      subscription_status: 'free',
      status: 'active',
      email_verified: false,
      phone_verified: false
    });
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone.includes(searchTerm);
    
    const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
    const matchesSubscription = filterSubscription === 'all' || client.subscription_status === filterSubscription;

    return matchesSearch && matchesStatus && matchesSubscription;
  });

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const currentClients = filteredClients.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return 'badge-success';
      case 'inactive': return 'badge-warning';
      case 'blocked': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const getSubscriptionBadge = (subscription: string) => {
    return subscription === 'premium' ? 'badge-premium' : 'badge-secondary';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Client Management" subtitle="Manage client accounts and subscriptions">
        <div className="loading-spinner" />
      </AdminLayout>
    );
  }

  // Calculate statistics
  const stats = {
    totalClients: clients.length,
    activeClients: clients.filter(c => c.status === 'active').length,
    premiumClients: clients.filter(c => c.subscription_status === 'premium').length,
    verifiedClients: clients.filter(c => c.email_verified && c.phone_verified).length
  };

  return (
    <AdminLayout title="Client Management" subtitle="Manage client accounts and subscriptions">
      <div className="fade-in">
        {/* Header Actions */}
        <div className="admin-card" style={{ marginBottom: '25px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
              {/* Search */}
              <div style={{ position: 'relative', minWidth: '300px' }}>
                <input
                  type="text"
                  placeholder="Search clients by name, email, or phone..."
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '40px' }}
                />
                <Search size={18} style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: 'var(--text-dark-gray)'
                }} />
              </div>

              {/* Status Filter */}
              <select 
                className="form-control" 
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ minWidth: '120px' }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="blocked">Blocked</option>
              </select>

              {/* Subscription Filter */}
              <select 
                className="form-control" 
                value={filterSubscription}
                onChange={(e) => setFilterSubscription(e.target.value)}
                style={{ minWidth: '120px' }}
              >
                <option value="all">All Plans</option>
                <option value="free">Free</option>
                <option value="premium">Premium</option>
              </select>

              {/* View Mode Toggle */}
              <div style={{ display: 'flex', gap: '5px' }}>
                <button 
                  className={viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}
                  onClick={() => setViewMode('grid')}
                  style={{ padding: '10px', minWidth: 'auto' }}
                >
                  <Grid3X3 size={18} />
                </button>
                <button 
                  className={viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}
                  onClick={() => setViewMode('list')}
                  style={{ padding: '10px', minWidth: 'auto' }}
                >
                  <List size={18} />
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn-secondary">
                <Download size={18} />
                Export
              </button>
              <button 
                className="btn-primary"
                onClick={() => setShowClientModal(true)}
              >
                <Plus size={18} />
                Add Client
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stat-cards" style={{ marginBottom: '25px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <Users size={24} />
            </div>
            <div className="stat-value">{stats.totalClients}</div>
            <div className="stat-label">Total Clients</div>
            <div style={{ fontSize: '12px', color: 'var(--accent-blue)', marginTop: '5px' }}>
              {stats.activeClients} active
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Star size={24} />
            </div>
            <div className="stat-value">{stats.premiumClients}</div>
            <div className="stat-label">Premium Clients</div>
            <div style={{ fontSize: '12px', color: 'var(--accent-orange)', marginTop: '5px' }}>
              {stats.totalClients > 0 ? ((stats.premiumClients / stats.totalClients) * 100).toFixed(1) : '0'}% conversion
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <CheckCircle size={24} />
            </div>
            <div className="stat-value">{stats.verifiedClients}</div>
            <div className="stat-label">Verified Clients</div>
            <div style={{ fontSize: '12px', color: 'var(--accent-green)', marginTop: '5px' }}>
              {stats.totalClients > 0 ? ((stats.verifiedClients / stats.totalClients) * 100).toFixed(1) : '0'}% verified
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <CreditCard size={24} />
            </div>
            <div className="stat-value">
              ${clients.reduce((sum, c) => sum + (c.total_spent || 0), 0).toFixed(0)}
            </div>
            <div className="stat-label">Total Revenue</div>
          </div>
        </div>

        {/* Clients Table/Grid */}
        <div className="admin-card">
          <div className="card-header">
            <div className="card-title">Clients</div>
            <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)' }}>
              Showing {currentClients.length} of {filteredClients.length} clients
            </div>
          </div>

          {viewMode === 'grid' ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
              gap: '20px' 
            }}>
              {currentClients.map(client => (
                <div 
                  key={client.id} 
                  className="admin-card" 
                  style={{ 
                    margin: 0, 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => openClientDetails(client)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      background: client.profile_picture ? `url(${client.profile_picture})` : 'var(--accent-blue)', 
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '18px',
                      fontWeight: '600'
                    }}>
                      {!client.profile_picture && `${client.first_name.charAt(0)}${client.last_name.charAt(0)}`}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
                        {client.first_name} {client.last_name}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                        <span className={`badge ${getStatusBadge(client.status)}`}>
                          {client.status}
                        </span>
                        <span className={`badge ${getSubscriptionBadge(client.subscription_status)}`}>
                          {client.subscription_status}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Mail size={14} />
                      {client.email}
                      {client.email_verified && <CheckCircle size={14} color="var(--accent-green)" />}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Phone size={14} />
                      {client.phone}
                      {client.phone_verified && <CheckCircle size={14} color="var(--accent-green)" />}
                    </div>
                    {client.address && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <MapPin size={14} />
                        {client.address}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-dark-gray)' }}>
                      {client.total_bookings || 0} bookings • ${client.total_spent || 0} spent
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        className="btn-secondary" 
                        style={{ padding: '6px', minWidth: 'auto' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditClient(client);
                        }}
                        title="Edit Client"
                      >
                        <Edit size={14} />
                      </button>
                      <button 
                        className="btn-danger" 
                        style={{ padding: '6px', minWidth: 'auto' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClient(client.id);
                        }}
                        title="Delete Client"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Contact</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Subscription</th>
                    <th>Bookings</th>
                    <th>Spent</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentClients.map(client => (
                    <tr key={client.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ 
                            width: '40px', 
                            height: '40px', 
                            background: client.profile_picture ? `url(${client.profile_picture})` : 'var(--accent-blue)', 
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}>
                            {!client.profile_picture && `${client.first_name.charAt(0)}${client.last_name.charAt(0)}`}
                          </div>
                          <div>
                            <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                              {client.first_name} {client.last_name}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                              ID: {client.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                            <Mail size={12} />
                            <span style={{ fontSize: '12px' }}>{client.email}</span>
                            {client.email_verified && <CheckCircle size={12} color="var(--accent-green)" />}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Phone size={12} />
                            <span style={{ fontSize: '12px' }}>{client.phone}</span>
                            {client.phone_verified && <CheckCircle size={12} color="var(--accent-green)" />}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '12px' }}>
                          {client.parish && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <MapPin size={12} />
                              {client.parish}
                            </div>
                          )}
                          {client.address && (
                            <div style={{ color: 'var(--text-medium-gray)', marginTop: '2px' }}>
                              {client.address.length > 30 ? `${client.address.substring(0, 30)}...` : client.address}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadge(client.status)}`}>
                          {client.status}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getSubscriptionBadge(client.subscription_status)}`}>
                          {client.subscription_status}
                        </span>
                      </td>
                      <td>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: '500' }}>{client.total_bookings || 0}</div>
                          <div style={{ fontSize: '10px', color: 'var(--text-medium-gray)' }}>
                            {client.completed_bookings || 0} completed
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: '500', color: 'var(--accent-green)' }}>
                          ${(client.total_spent || 0).toFixed(2)}
                        </div>
                      </td>
                      <td style={{ fontSize: '12px' }}>
                        {formatDate(client.created_at)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button 
                            className="btn-secondary" 
                            style={{ padding: '6px', minWidth: 'auto' }}
                            onClick={() => openClientDetails(client)}
                            title="View Details"
                          >
                            <Eye size={14} />
                          </button>
                          <button 
                            className="btn-secondary" 
                            style={{ padding: '6px', minWidth: 'auto' }}
                            onClick={() => openEditClient(client)}
                            title="Edit Client"
                          >
                            <Edit size={14} />
                          </button>
                          <button 
                            className="btn-danger" 
                            style={{ padding: '6px', minWidth: 'auto' }}
                            onClick={() => handleDeleteClient(client.id)}
                            title="Delete Client"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '20px',
            padding: '20px 0'
          }}>
            <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)' }}>
              Page {currentPage} of {totalPages}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="btn-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              >
                Previous
              </button>
              <button 
                className="btn-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Client Modal */}
        {showClientModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="admin-card" style={{ 
              width: '90%', 
              maxWidth: '600px', 
              margin: 0,
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div className="card-header">
                <div className="card-title">
                  {editingClient ? 'Edit Client' : 'Add New Client'}
                </div>
              </div>
              
              <form onSubmit={handleClientSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={clientForm.first_name}
                      onChange={(e) => setClientForm({ ...clientForm, first_name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={clientForm.last_name}
                      onChange={(e) => setClientForm({ ...clientForm, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={clientForm.email}
                      onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={clientForm.phone}
                      onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={clientForm.address}
                    onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                  `  <label className="form-label">City</label>`
                    <input
                      type="text"
                      className="form-control"
                      value={clientForm.parish}
                      onChange={(e) => setClientForm({ ...clientForm, parish: e.target.value })}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Subscription</label>
                    <select
                      className="form-control"
                      value={clientForm.subscription_status}
                      onChange={(e) => setClientForm({ ...clientForm, subscription_status: e.target.value as 'free' | 'premium' })}
                    >
                      <option value="free">Free</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      className="form-control"
                      value={clientForm.status}
                      onChange={(e) => setClientForm({ ...clientForm, status: e.target.value as 'active' | 'inactive' | 'blocked' })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={() => {
                      setShowClientModal(false);
                      setEditingClient(null);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingClient ? 'Update' : 'Create'} Client
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Client Details Modal */}
        {showDetailsModal && selectedClient && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="admin-card" style={{ 
              width: '90%', 
              maxWidth: '800px', 
              margin: 0,
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div className="card-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="card-title">Client Details</div>
                  <button 
                    className="btn-secondary"
                    onClick={() => setShowDetailsModal(false)}
                  >
                    <XCircle size={18} />
                    Close
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '30px' }}>
                {/* Client Info */}
                <div>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{ 
                      width: '100px', 
                      height: '100px', 
                      background: selectedClient.profile_picture ? `url(${selectedClient.profile_picture})` : 'var(--accent-blue)', 
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '24px',
                      fontWeight: '600',
                      margin: '0 auto 15px'
                    }}>
                      {!selectedClient.profile_picture && `${selectedClient.first_name.charAt(0)}${selectedClient.last_name.charAt(0)}`}
                    </div>
                    <h3 style={{ marginBottom: '5px' }}>{selectedClient.first_name} {selectedClient.last_name}</h3>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '10px' }}>
                      <span className={`badge ${getStatusBadge(selectedClient.status)}`}>
                        {selectedClient.status}
                      </span>
                      <span className={`badge ${getSubscriptionBadge(selectedClient.subscription_status)}`}>
                        {selectedClient.subscription_status}
                      </span>
                    </div>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '10px' }}>Contact Information</h4>
                    <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <Mail size={16} />
                        {selectedClient.email}
                        {selectedClient.email_verified && <CheckCircle size={16} color="var(--accent-green)" />}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <Phone size={16} />
                        {selectedClient.phone}
                        {selectedClient.phone_verified && <CheckCircle size={16} color="var(--accent-green)" />}
                      </div>
                      {selectedClient.address && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <MapPin size={16} />
                          {selectedClient.address}, {selectedClient.parish}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Statistics & Activity */}
                <div>
                  <div style={{ marginBottom: '25px' }}>
                    <h4 style={{ marginBottom: '15px' }}>Statistics</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div className="admin-card" style={{ margin: 0, textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent-blue)' }}>
                          {selectedClient.total_bookings || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>Total Bookings</div>
                      </div>
                      <div className="admin-card" style={{ margin: 0, textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent-green)' }}>
                          {selectedClient.completed_bookings || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>Completed</div>
                      </div>
                      <div className="admin-card" style={{ margin: 0, textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent-red)' }}>
                          {selectedClient.cancelled_bookings || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>Cancelled</div>
                      </div>
                      <div className="admin-card" style={{ margin: 0, textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent-purple)' }}>
                          ${(selectedClient.total_spent || 0).toFixed(0)}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>Total Spent</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 style={{ marginBottom: '15px' }}>Account Information</h4>
                    <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                      <div><strong>Client ID:</strong> {selectedClient.id}</div>
                      <div><strong>Joined:</strong> {formatDate(selectedClient.created_at)}</div>
                      <div><strong>Last Updated:</strong> {formatDate(selectedClient.updated_at)}</div>
                      {selectedClient.last_booking_date && (
                        <div><strong>Last Booking:</strong> {formatDate(selectedClient.last_booking_date)}</div>
                      )}
                      {selectedClient.subscription_start_date && (
                        <div><strong>Subscription Start:</strong> {formatDate(selectedClient.subscription_start_date)}</div>
                      )}
                      {selectedClient.subscription_end_date && (
                        <div><strong>Subscription End:</strong> {formatDate(selectedClient.subscription_end_date)}</div>
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: '25px', display: 'flex', gap: '10px' }}>
                    <button 
                      className="btn-primary"
                      onClick={() => {
                        setShowDetailsModal(false);
                        openEditClient(selectedClient);
                      }}
                    >
                      <Edit size={18} />
                      Edit Client
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Clients; 